import { useEffect, useRef } from 'react';

/**
 * Shared scroll handler for the sticky header used across MainLayout,
 * PrivatePage and SubLayout.
 *
 * Toggles visibility / position of the main header, sub header and
 * promotion banner based on scroll direction inside #scrollableDiv.
 *
 * Uses refs to preserve lastScrollTop / currentTop across effect re-runs
 * (e.g. when `device` changes) so the header position does not reset.
 *
 * @param {string} device - current device type from useDeviceType()
 */
const useScrollHeader = device => {
  const lastScrollTopRef = useRef(0);
  const currentTopRef = useRef(73);

  useEffect(() => {
    const scrollableDiv = document.getElementById('scrollableDiv');
    if (!scrollableDiv) return;

    const handleScroll = () => {
      if (device !== 'desktop') return;

      const mainDiv = document.getElementById('main-action-header');
      const subDiv = document.getElementById('sub-header');
      const promotionDiv = document.getElementById('promotion-header');

      const scrollTop = scrollableDiv?.scrollTop || 0;
      const scrollDelta = scrollTop - lastScrollTopRef.current;

      // Tính toán top cho subDiv dựa trên hướng scroll
      if (subDiv) {
        if (scrollDelta > 0) {
          // Scroll xuống: giảm top
          currentTopRef.current = Math.max(
            0,
            currentTopRef.current - scrollDelta
          );
          subDiv.classList.add('lg:top-0');
          scrollableDiv.classList.add('lg:mt-[72px]');

          subDiv.classList.remove('lg:top-[73px]');
          scrollableDiv.classList.remove('lg:mt-[150px]');
        } else if (scrollDelta < 0) {
          // Scroll lên: tăng top
          currentTopRef.current = Math.min(
            73,
            currentTopRef.current - scrollDelta
          );
          subDiv.classList.add('lg:top-[73px]');
          scrollableDiv.classList.add('lg:mt-[150px]');

          subDiv.classList.remove('lg:top-0');
          scrollableDiv.classList.remove('lg:mt-[72px]');
        }

        // Điều chỉnh opacity
        if (currentTopRef.current === 0) {
          subDiv.classList.add('lg:opacity-0');
          scrollableDiv.classList.add('lg:mt-[72px]');
        } else {
          subDiv.classList.add('lg:opacity-100');
        }
      }

      lastScrollTopRef.current = scrollTop;

      if (currentTopRef.current === 0) {
        mainDiv?.classList?.remove('opacity-0');
        mainDiv?.classList?.add('opacity-100');
        mainDiv?.classList?.remove('z-[-1000]');
        mainDiv?.classList?.add('z-[1000]');

        promotionDiv?.classList?.add('opacity-0');
        promotionDiv?.classList?.remove('opacity-100');
      } else {
        mainDiv?.classList?.remove('opacity-100');
        mainDiv?.classList?.add('opacity-0');
        mainDiv?.classList?.remove('z-[1000]');
        mainDiv?.classList?.add('z-[-1000]');

        promotionDiv?.classList?.add('opacity-100');
        promotionDiv?.classList?.remove('opacity-0');
      }
    };

    scrollableDiv.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollableDiv.removeEventListener('scroll', handleScroll);
  }, [device]);
};

export default useScrollHeader;
